import type { SdkHooks } from '@Pages/pageRuntime/sdk/types';
import {
  sdkMemberLoaders,
  suspendingModuleRead,
  type ModuleLoader,
  type RuntimeLoad,
  type RuntimeLoader
} from '@Pages/pageRuntime/sdkMemberLoaders';

type LazyHook<THook> = {
  hook: THook;
  loader: RuntimeLoader;
};

// Throw the import promise so PageRenderer's Suspense boundary can load optional hooks on demand.
// oxlint-disable-next-line typescript/no-explicit-any -- the facade keeps each hook's generic signature
function lazyHook<TModule, THook extends (...args: any[]) => any>(
  loader: ModuleLoader<TModule>,
  pick: (module: TModule) => THook
): LazyHook<THook> {
  const read = suspendingModuleRead(loader);
  const facade = ((...args) => pick(read())(...args)) as THook;

  return { hook: facade, loader };
}

const lazyHooks = {
  useCreateResourceRecordForm: lazyHook(
    sdkMemberLoaders['hooks.useCreateResourceRecordForm'],
    (module): SdkHooks['useCreateResourceRecordForm'] => module.useCreateResourceRecordForm
  ),
  useUpdateResourceRecordForm: lazyHook(
    sdkMemberLoaders['hooks.useUpdateResourceRecordForm'],
    (module): SdkHooks['useUpdateResourceRecordForm'] => module.useUpdateResourceRecordForm
  ),
  useWorkflowForm: lazyHook(
    sdkMemberLoaders['hooks.useWorkflowForm'],
    (module): SdkHooks['useWorkflowForm'] => module.useWorkflowForm
  ),
  useWorkspaceFieldsForm: lazyHook(
    sdkMemberLoaders['hooks.useWorkspaceFieldsForm'],
    (module): SdkHooks['useWorkspaceFieldsForm'] => module.useWorkspaceFieldsForm
  ),
  useDataTable: lazyHook(
    sdkMemberLoaders['hooks.useDataTable'],
    (module): SdkHooks['useDataTable'] => module.useDataTable
  ),
  useResourceDataTable: lazyHook(
    sdkMemberLoaders['hooks.useResourceDataTable'],
    (module): SdkHooks['useResourceDataTable'] => module.useResourceDataTable
  ),
  useCalendarView: lazyHook(
    sdkMemberLoaders['hooks.useCalendarView'],
    (module): SdkHooks['useCalendarView'] => module.useCalendarView
  ),
  useCalendarEvents: lazyHook(
    sdkMemberLoaders['hooks.useCalendarEvents'],
    (module): SdkHooks['useCalendarEvents'] => module.useCalendarEvents
  ),
  useNewCalendarEvent: lazyHook(
    sdkMemberLoaders['hooks.useNewCalendarEvent'],
    (module): SdkHooks['useNewCalendarEvent'] => module.useNewCalendarEvent
  ),
  useCalendarSelection: lazyHook(
    sdkMemberLoaders['hooks.useCalendarSelection'],
    (module): SdkHooks['useCalendarSelection'] => module.useCalendarSelection
  )
};

export const useCreateResourceRecordForm = lazyHooks.useCreateResourceRecordForm.hook;
export const useUpdateResourceRecordForm = lazyHooks.useUpdateResourceRecordForm.hook;
export const useWorkflowForm = lazyHooks.useWorkflowForm.hook;
export const useWorkspaceFieldsForm = lazyHooks.useWorkspaceFieldsForm.hook;
export const useDataTable = lazyHooks.useDataTable.hook;
export const useResourceDataTable = lazyHooks.useResourceDataTable.hook;
export const useCalendarView = lazyHooks.useCalendarView.hook;
export const useCalendarEvents = lazyHooks.useCalendarEvents.hook;
export const useNewCalendarEvent = lazyHooks.useNewCalendarEvent.hook;
export const useCalendarSelection = lazyHooks.useCalendarSelection.hook;

export function prepareLazyHooksForSource(
  sources: Array<string | null | undefined>,
  load: RuntimeLoad
): Promise<void> | null {
  const loading: Promise<void>[] = [];

  for (const [name, { loader }] of Object.entries(lazyHooks)) {
    const hookLoad = sources.some((source) => source && source.includes(name))
      ? load(loader)
      : null;

    if (hookLoad !== null) {
      loading.push(hookLoad.then(() => undefined));
    }
  }

  return loading.length > 0 ? Promise.all(loading).then(() => undefined) : null;
}
